import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  X,
  ChevronRight,
  ChevronLeft,
  Sparkles,
  Megaphone,
  User,
  Calendar,
  Clock,
  ThumbsUp,
  ThumbsDown,
  Meh,
  Search,
  Maximize2,
  Minimize2,
  Pin,
} from "lucide-react";
import DOMPurify from "dompurify";
import { kbService } from "../utils/kbService";
import { getFallbackBackView } from "../utils/navigationHelper";

function News({
  setView,
  setActiveIcon,
  setupDetails,
  setIsOpen,
  isExpanded,
  setIsExpanded,
  widgetConfigs,
  selectedArticle,
  setSelectedArticle,
}) {
  const partnerId =
    widgetConfigs?.uniquePartnerId ||
    setupDetails?.uniquePartnerId ||
    localStorage.getItem("uniquePartnerId");

  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openedArticle, setOpenedArticle] = useState(selectedArticle || null);
  const [feedbackGiven, setFeedbackGiven] = useState(null);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const scrollRef = useRef(null);

  const color1 = setupDetails?.widgetCustomizations?.color1 || "#0C2A4D";
  const color2 = setupDetails?.widgetCustomizations?.color2 || "#2060EA";
  const primaryColor =
    setupDetails?.widgetCustomizations?.uiColor ||
    setupDetails?.widgetCustomizations?.color2 ||
    "#2563eb";

  // Fetch only news and announcement articles
  useEffect(() => {
    if (!partnerId) {
      setLoading(false);
      return;
    }

    let isMounted = true;
    setLoading(true);

    kbService
      .getAnnouncements(partnerId)
      .then((data) => {
        if (!isMounted) return;
        setArticles(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error("Error fetching news articles:", err);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [partnerId]);

  // If selectedArticle prop changes externally, open and expand
  useEffect(() => {
    if (selectedArticle) {
      setOpenedArticle(selectedArticle);
      if (setIsExpanded) setIsExpanded(true);
      const articleId = selectedArticle._id || selectedArticle.id;
      if (articleId && partnerId) {
        kbService.trackView(articleId, partnerId);
      }
    }
  }, [selectedArticle, setIsExpanded, partnerId]);

  // Scroll to top when opening an article or returning to list
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
    setFeedbackGiven(null);
  }, [openedArticle]);

  const handleOpenArticle = (art) => {
    setOpenedArticle(art);
    if (setSelectedArticle) setSelectedArticle(art);
    if (setIsExpanded) setIsExpanded(true); // Automatically expand the widget window

    const articleId = art?._id || art?.id;
    if (articleId && partnerId) {
      kbService.trackView(articleId, partnerId);
    }
  };

  const handleCloseArticle = () => {
    setOpenedArticle(null);
    if (setSelectedArticle) setSelectedArticle(null);
    if (setIsExpanded) setIsExpanded(false); // Collapse back to default width
  };

  // Calculate read time
  const getReadTime = (content) => {
    if (!content) return 1;
    const text = typeof content === "string" ? content : content?.text || "";
    const cleanText = text.replace(/<[^>]*>/g, "");
    const wordCount = cleanText.trim().split(/\s+/).filter(Boolean).length;
    return Math.max(1, Math.ceil(wordCount / 200));
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return "";
    }
  };

  // Handle article feedback reaction
  const handleReaction = async (type) => {
    if (!openedArticle?._id || !partnerId || feedbackGiven) return;
    setFeedbackGiven(type);
    await kbService.addReaction(openedArticle._id, partnerId, type);
  };

  // Filter articles: only search news and announcement articles
  const filteredArticles = useMemo(() => {
    if (!searchQuery.trim()) return articles;
    const q = searchQuery.toLowerCase().trim();
    return articles.filter((art) => {
      const isNewsOrAnnouncement =
        art.articleType === "news" ||
        art.articleType === "announcement" ||
        !art.articleType;
      if (!isNewsOrAnnouncement) return false;

      const title = (art.title || "").toLowerCase();
      const desc = (art.description || "").toLowerCase();
      const text =
        typeof art.content === "string"
          ? art.content.toLowerCase()
          : (art.content?.text || "").toLowerCase();

      return title.includes(q) || desc.includes(q) || text.includes(q);
    });
  }, [articles, searchQuery]);

  // Sanitized body HTML
  const sanitizedArticleHtml = useMemo(() => {
    if (!openedArticle) return "";
    const rawHtml =
      openedArticle.content?.text ||
      (typeof openedArticle.content === "string" ? openedArticle.content : "") ||
      openedArticle.description ||
      "";
    return DOMPurify.sanitize(rawHtml, {
      ADD_TAGS: ["iframe"],
      ADD_ATTR: [
        "allow",
        "allowfullscreen",
        "frameborder",
        "scrolling",
        "target",
      ],
    });
  }, [openedArticle]);

  return (
    <div className="flex flex-col h-full w-full bg-white relative overflow-hidden">
      {/* ========================================================= */}
      {/* 1. TOP NAVIGATION HEADER (With Back, Title, Expand & Close)*/}
      {/* ========================================================= */}
      <div className="flex items-center justify-between px-4 h-14 border-b border-slate-100 shrink-0 sticky top-0 z-30 backdrop-blur-md bg-white/95">
        <button
          type="button"
          onClick={() => {
            if (openedArticle) {
              handleCloseArticle();
            } else {
              const fallback = getFallbackBackView(setupDetails);
              setView(fallback.view);
              if (setActiveIcon) setActiveIcon(fallback.icon);
            }
          }}
          className="w-8 h-8 rounded-full flex items-center justify-center text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-all border-none outline-none cursor-pointer"
          title="Back"
        >
          <ChevronLeft className="w-5 h-5 stroke-[2.2]" />
        </button>

        <div className="font-bold text-slate-900 text-sm tracking-tight text-center truncate px-2 flex-1">
          {openedArticle ? openedArticle.title : "News"}
        </div>

        <div className="flex items-center gap-1 min-w-[32px] justify-end">
          {setIsExpanded && (
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="w-8 h-8 rounded-full flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-all border-none outline-none cursor-pointer"
              title={isExpanded ? "Collapse" : "Expand"}
            >
              {isExpanded ? (
                <Minimize2 className="w-4 h-4 text-slate-500 hover:text-slate-800" />
              ) : (
                <Maximize2 className="w-4 h-4 text-slate-500 hover:text-slate-800" />
              )}
            </button>
          )}

          <button
            type="button"
            onClick={() => {
              if (setIsOpen) setIsOpen(false);
              if (setIsExpanded) setIsExpanded(false);
            }}
            className="w-8 h-8 rounded-full flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-all border-none outline-none cursor-pointer"
            title="Close"
          >
            <X className="w-4 h-4 stroke-[2.2]" />
          </button>
        </div>
      </div>

      {/* Main Scrollable View */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto overflow-x-hidden p-4 pb-24 scroll-smooth"
      >
        {openedArticle ? (
          /* ========================================================= */
          /* 2. ARTICLE DETAIL VIEW (EXPANDED LIKE HELP ARTICLE)       */
          /* ========================================================= */
          <div className="animate-in fade-in duration-200 max-w-2xl mx-auto">
            {/* Back to all news breadcrumb */}
            <button
              onClick={handleCloseArticle}
              className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-blue-600 transition-colors mb-3 border-none bg-transparent p-0 cursor-pointer"
            >
              <ChevronLeft className="w-4 h-4" />
              <span>Back to all news</span>
            </button>

            {/* Banner cover image if present */}
            {(openedArticle.bannerImage ||
              openedArticle.imageUrl ||
              (openedArticle.content?.images &&
                openedArticle.content.images[0])) && (
              <div className="relative h-56 w-full rounded-2xl overflow-hidden mb-4 bg-slate-950 border border-slate-200/80 shadow-xs">
                <img
                  src={
                    openedArticle.bannerImage ||
                    openedArticle.imageUrl ||
                    openedArticle.content.images[0]
                  }
                  alt={openedArticle.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Meta tags */}
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-blue-50 text-blue-700 border border-blue-100 flex items-center gap-1">
                {openedArticle.isFeatured && <Pin className="w-3 h-3 text-blue-700 fill-blue-700 rotate-45" />}
                <span>{openedArticle.isFeatured ? "News" : "News"}</span>
              </span>

              {openedArticle.createdAt && (
                <span className="flex items-center gap-1 text-[11px] text-slate-400">
                  <Calendar className="w-3 h-3" />
                  {formatDate(openedArticle.createdAt)}
                </span>
              )}

              <span className="flex items-center gap-1 text-[11px] text-slate-400">
                <Clock className="w-3 h-3" />
                {getReadTime(openedArticle.content)} min read
              </span>
            </div>

            {/* Article Headline */}
            <h1 className="text-xl font-bold text-slate-900 leading-snug mb-3">
              {openedArticle.title}
            </h1>

            {/* Author */}
            {openedArticle.writer && (
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-100 text-xs text-slate-500">
                <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-bold text-[10px]">
                  {openedArticle.writer.charAt(0).toUpperCase()}
                </div>
                <span>
                  By <strong className="text-slate-700">{openedArticle.writer}</strong>
                </span>
              </div>
            )}

            {/* Article Body Content */}
            <div className="article-body text-slate-700 text-sm leading-relaxed mb-8">
              <style>{`
                .article-body h1 { font-size: 18px; font-weight: 700; color: #0f172a; margin-top: 16px; margin-bottom: 8px; }
                .article-body h2 { font-size: 15px; font-weight: 700; color: #0f172a; margin-top: 14px; margin-bottom: 6px; }
                .article-body h3 { font-size: 14px; font-weight: 600; color: #1e293b; margin-top: 12px; margin-bottom: 4px; }
                .article-body p { margin-bottom: 12px; }
                .article-body strong { font-weight: 600; color: #0f172a; }
                .article-body img {
                  max-width: 100% !important;
                  height: auto !important;
                  border-radius: 12px;
                  margin: 12px 0;
                  border: 1px solid #e2e8f0;
                }
                .article-body ul, .article-body ol { padding-left: 20px; margin-bottom: 12px; }
                .article-body li { margin-bottom: 4px; }
                .article-body a { color: ${primaryColor}; text-decoration: underline; word-break: break-all; }
                .article-body blockquote {
                  border-left: 3px solid ${primaryColor};
                  padding-left: 12px;
                  margin: 12px 0;
                  color: #64748b;
                  font-style: italic;
                }
                .article-body table { width: 100% !important; border-collapse: collapse; margin: 12px 0; font-size: 12px; }
                .article-body th, .article-body td { border: 1px solid #e2e8f0; padding: 6px 8px; text-align: left; }
                .article-body th { background-color: #f8fafc; font-weight: 600; }
                .article-body pre { background-color: #f1f5f9; padding: 8px 12px; border-radius: 6px; overflow-x: auto; margin: 10px 0; font-size: 11px; }
              `}</style>
              <div dangerouslySetInnerHTML={{ __html: sanitizedArticleHtml }} />
            </div>

            {/* Helpful feedback card */}
            <div className="mt-8 pt-4 border-t border-slate-100 bg-slate-50/70 rounded-2xl p-4 text-center">
              <div className="text-xs font-bold text-slate-800 mb-1">
                Was this update helpful?
              </div>
              <div className="text-[11px] text-slate-500 mb-3">
                Let us know what you think.
              </div>

              {feedbackGiven ? (
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold">
                  <span>✓</span>
                  <span>Thank you for your feedback!</span>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <button
                    onClick={() => handleReaction("happy")}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white hover:bg-slate-100 border border-slate-200 text-slate-700 text-xs font-semibold transition-all hover:scale-105 active:scale-95 cursor-pointer"
                  >
                    <ThumbsUp className="w-3.5 h-3.5 text-emerald-600" />
                    <span>Yes</span>
                  </button>
                  <button
                    onClick={() => handleReaction("neutral")}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white hover:bg-slate-100 border border-slate-200 text-slate-700 text-xs font-semibold transition-all hover:scale-105 active:scale-95 cursor-pointer"
                  >
                    <Meh className="w-3.5 h-3.5 text-amber-500" />
                    <span>Somewhat</span>
                  </button>
                  <button
                    onClick={() => handleReaction("sad")}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white hover:bg-slate-100 border border-slate-200 text-slate-700 text-xs font-semibold transition-all hover:scale-105 active:scale-95 cursor-pointer"
                  >
                    <ThumbsDown className="w-3.5 h-3.5 text-rose-500" />
                    <span>No</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* ========================================================= */
          /* 3. NEWS FEED VIEW (GLEAP STYLE WITH SEARCH)               */
          /* ========================================================= */
          <div className="space-y-4 max-w-2xl mx-auto">
            {/* Search Bar matching Help Center */}
            <div className="relative">
              <div className="bg-slate-100/80 hover:bg-slate-100 rounded-2xl px-3 py-2 flex items-center gap-2.5 transition-all border border-transparent focus-within:border-slate-300 focus-within:bg-white focus-within:shadow-xs">
                <Search className="w-4 h-4 text-slate-400 shrink-0" />
                <input
                  type="text"
                  placeholder="Search news..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full text-xs text-slate-800 placeholder-slate-400 border-none outline-none bg-transparent font-normal"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    className="text-slate-400 hover:text-slate-600 p-0 border-none bg-transparent cursor-pointer"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>

            {loading ? (
              /* Shimmer Skeletons */
              <div className="space-y-4">
                {[1, 2].map((i) => (
                  <div
                    key={i}
                    className="bg-white rounded-2xl border border-slate-200/80 overflow-hidden shadow-xs animate-pulse"
                  >
                    <div className="h-44 bg-slate-200 w-full" />
                    <div className="p-4 space-y-2">
                      <div className="h-4 bg-slate-200 rounded w-2/3" />
                      <div className="h-3 bg-slate-100 rounded w-full" />
                      <div className="h-3 bg-slate-100 rounded w-4/5" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredArticles.length > 0 ? (
              /* News Cards */
              filteredArticles.map((art) => {
                const banner =
                  art.bannerImage ||
                  art.imageUrl ||
                  (art.content?.images && art.content.images[0]);
                const excerpt =
                  art.description ||
                  (art.content?.text
                    ? art.content.text.replace(/<[^>]*>/g, "").trim()
                    : "");

                return (
                  <div
                    key={art._id || art.slug}
                    onClick={() => handleOpenArticle(art)}
                    className="bg-white rounded-2xl border border-slate-200/80 shadow-xs hover:shadow-md transition-all duration-300 overflow-hidden cursor-pointer group"
                  >
                    {/* Cover image or glowing gradient header */}
                    {banner ? (
                      <div className="relative h-44 w-full overflow-hidden bg-slate-950">
                        <img
                          src={banner}
                          alt={art.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
                        <div className="absolute top-3 left-3 bg-black/50 backdrop-blur-md px-2.5 py-1 rounded-full flex items-center gap-1.5 text-[10px] font-bold text-white uppercase tracking-wider border border-white/20">
                          {art.isFeatured ? (
                            <Pin className="w-3 h-3 text-amber-300 fill-amber-300 rotate-45" />
                          ) : (
                            <Sparkles className="w-3 h-3 text-amber-300" />
                          )}
                          <span>
                            {art.isFeatured ? " News" : "News"}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div
                        className="h-32 p-4 text-white flex flex-col justify-between relative overflow-hidden"
                        style={{
                          background: `linear-gradient(135deg, ${color1} 0%, ${color2} 100%)`,
                        }}
                      >
                        <div className="flex items-center gap-1.5 text-[9px] font-bold text-white/90 uppercase tracking-widest relative z-10">
                          <Sparkles className="w-3.5 h-3.5 text-white/90" />
                          <span>
                            {art.articleType === "news" ? "News" : "Announcement"}
                          </span>
                        </div>
                        <div className="relative z-10">
                          <div className="text-base font-bold text-white leading-snug line-clamp-2">
                            {art.title}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Card Body */}
                    <div className="p-4 flex items-center justify-between bg-white">
                      <div className="pr-3 min-w-0 flex-1">
                        <h3 className="text-base font-bold text-slate-900 leading-snug group-hover:text-blue-600 transition-colors">
                          {art.title}
                        </h3>
                        {excerpt && (
                          <p className="text-xs text-slate-500 mt-1 line-clamp-2 leading-relaxed">
                            {excerpt}
                          </p>
                        )}
                        {art.createdAt && (
                          <span className="text-[10px] text-slate-400 mt-1.5 block">
                            {formatDate(art.createdAt)}
                          </span>
                        )}
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400 shrink-0 group-hover:text-blue-600 group-hover:translate-x-0.5 transition-all ml-2" />
                    </div>
                  </div>
                );
              })
            ) : (
              /* Empty state */
              <div className="py-16 text-center bg-slate-50/50 rounded-2xl border border-dashed border-slate-200 p-6">
                <div className="w-12 h-12 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center mx-auto mb-3">
                  {searchQuery ? <Search className="w-6 h-6" /> : <Megaphone className="w-6 h-6" />}
                </div>
                <h3 className="text-sm font-bold text-slate-800">
                  {searchQuery ? "No matching news or announcements" : "No news yet"}
                </h3>
                <p className="text-xs text-slate-500 mt-1 max-w-xs mx-auto">
                  {searchQuery
                    ? `No articles found matching "${searchQuery}". Try different keywords.`
                    : "Stay tuned! New feature releases and announcements will appear here."}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Image Zoom Modal */}
      {zoomedImage && (
        <div
          onClick={() => setZoomedImage(null)}
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 backdrop-blur-xs cursor-zoom-out"
        >
          <img
            src={zoomedImage}
            alt="Zoomed preview"
            className="max-w-full max-h-[90vh] rounded-lg shadow-2xl object-contain"
          />
        </div>
      )}
    </div>
  );
}

export default News;
